/**
 * Styled components for the wedding website
 * This file contains reusable styled components built with Emotion
 */

import { styled } from '@mui/material/styles';
import { Box, Button, Typography, Container, Card, CardContent, CardMedia } from '@mui/material';
import { colors, borderRadius, typography, shadows } from './constants';

// Styled Buttons
export const PrimaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  borderRadius: borderRadius.button,
  fontFamily: typography.fontFamilies.secondary,
  backgroundColor: colors.secondary.darkSolid,
  color: '#fff',
  '&:hover': {
    backgroundColor: colors.secondary.darkHover,
  },
  textTransform: 'none',
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  borderRadius: borderRadius.button,
  fontFamily: typography.fontFamilies.secondary,
  textTransform: 'none',
}));

export const WideButton = styled(Button)(({ theme }) => ({
  width: '90%',
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  borderRadius: borderRadius.button,
  fontFamily: typography.fontFamilies.secondary,
  backgroundColor: colors.secondary.darkSolid,
  color: '#fff',
  '&:hover': {
    backgroundColor: colors.secondary.darkHover,
  },
  textTransform: 'none',
}));

// Styled Typography
export const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontFamily: typography.fontFamilies.primary,
  fontWeight: typography.fontWeights.bold,
  textAlign: 'center',
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontFamily: typography.fontFamilies.primary,
  fontWeight: typography.fontWeights.bold,
}));

export const Subtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontFamily: typography.fontFamilies.primary,
  fontWeight: typography.fontWeights.semiBold,
}));

export const BodyText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontFamily: typography.fontFamilies.primary,
  textAlign: 'left',
}));

export const CenteredBodyText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  maxWidth: '800px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

// Styled Containers
export const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  maxWidth: '800px !important',
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const CenteredFlexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

// Styled Cards
export const StyledCard = styled(Card)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: borderRadius.card,
  boxShadow: shadows.card,
  backgroundColor: colors.background.card,
}));

export const StyledCardWithButton = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: borderRadius.card,
  boxShadow: shadows.card,
  backgroundColor: colors.background.card,
  position: 'relative',
  paddingBottom: theme.spacing(10), // Add padding at bottom for the button
}));

export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
}));

export const StyledCardMedia = styled(CardMedia)({
  objectFit: 'cover',
});

export const CardButtonContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  bottom: 20,
  left: 0,
  right: 0,
}));

// Hero Section Components
export const HeroSection = styled(Box)(({ theme }) => ({
  height: 'auto',
  minHeight: '90vh',
  [theme.breakpoints.down('sm')]: {
    minHeight: '100vh',
  },
  width: '100vw',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
}));

export const HeroContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  backgroundColor: colors.background.overlay,
  borderRadius: borderRadius.medium,
  padding: theme.spacing(4),
  margin: theme.spacing(4),
  backdropFilter: 'blur(2px)',
  maxWidth: '600px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    maxWidth: '90vw',
  },
}));

// Section Header
export const SectionHeader = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(225, 213, 203, 0.3)',
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: 20,
  marginBottom: theme.spacing(2),
  width: '100%',
}));

// Responsive Image
export const ResponsiveImage = styled(Box)(() => ({
  width: '100%',
  height: 'auto',
  borderRadius: borderRadius.medium,
}));

// Form Components
export const FormContainer = styled(Box)(() => ({
  textAlign: 'center',
  width: '100%',
}));

export const FormSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(6),
}));

export const FormActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(4),
}));
